/**
 * NavigationConfigPanel Component
 * 
 * Configuration panel for customizing navigation items visibility and order
 */

import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { NavItemConfig } from '@/types/navigation';
import { Checkbox } from '@/components/ui/Checkbox';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  LayoutDashboard,
  Users,
  Calendar,
  BarChart3,
  MessageSquare,
  Settings,
} from 'lucide-react';

interface NavigationConfigPanelProps {
  items: NavItemConfig[];
  onToggleVisibility: (itemId: string) => void;
  onReorder: (itemIds: string[]) => void;
}

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Users,
  Calendar,
  BarChart3,
  MessageSquare,
  Settings,
};

interface SortableNavItemProps {
  item: NavItemConfig;
  onToggle: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const SortableNavItem: React.FC<SortableNavItemProps> = ({
  item,
  onToggle,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = iconMap[item.iconName] || LayoutDashboard;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 px-4 py-3 mb-1 rounded-lg transition-all',
        'bg-white/5 border border-white/10',
        isDragging && 'z-50'
      )}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-white/60 hover:text-white/90 transition-colors"
        title="Drag to reorder"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          className="w-4 h-4"
        >
          <circle cx="4" cy="4" r="1.5" />
          <circle cx="12" cy="4" r="1.5" />
          <circle cx="4" cy="8" r="1.5" />
          <circle cx="12" cy="8" r="1.5" />
          <circle cx="4" cy="12" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
        </svg>
      </div>

      {/* Checkbox */}
      <Checkbox
        checked={item.visible}
        onChange={onToggle}
        className="flex-shrink-0"
      />

      {/* Icon */}
      <IconComponent className="w-5 h-5 text-white/90 flex-shrink-0" />

      {/* Label */}
      <span className="text-sm text-white/90 flex-1">{item.label}</span>

      {/* Move buttons */}
      <div className="flex flex-col gap-1">
        <button
          onClick={onMoveUp}
          disabled={!canMoveUp}
          className={cn(
            'p-1 rounded text-white/60 hover:text-white/90 hover:bg-white/10 transition-colors',
            'disabled:opacity-30 disabled:cursor-not-allowed'
          )}
          title="Move up"
        >
          <ArrowUp className="w-3 h-3" />
        </button>
        <button
          onClick={onMoveDown}
          disabled={!canMoveDown}
          className={cn(
            'p-1 rounded text-white/60 hover:text-white/90 hover:bg-white/10 transition-colors',
            'disabled:opacity-30 disabled:cursor-not-allowed'
          )}
          title="Move down"
        >
          <ArrowDown className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export const NavigationConfigPanel: React.FC<NavigationConfigPanelProps> = ({
  items,
  onToggleVisibility,
  onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const reorderedItems = arrayMove(items, oldIndex, newIndex);
      const itemIds = reorderedItems.map((item) => item.id);
      onReorder(itemIds);
    }
  };

  const handleMoveUp = (itemId: string) => {
    const index = items.findIndex((item) => item.id === itemId);
    if (index > 0) {
      const reorderedItems = arrayMove(items, index, index - 1);
      const itemIds = reorderedItems.map((item) => item.id);
      onReorder(itemIds);
    }
  };

  const handleMoveDown = (itemId: string) => {
    const index = items.findIndex((item) => item.id === itemId);
    if (index < items.length - 1) {
      const reorderedItems = arrayMove(items, index, index + 1);
      const itemIds = reorderedItems.map((item) => item.id);
      onReorder(itemIds);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
        <h3 className="text-sm font-semibold text-white">Configure Navigation</h3>
        <p className="text-xs text-white/60 mt-1">
          Select items to show and drag to reorder
        </p>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto px-2 py-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item, index) => (
              <SortableNavItem
                key={item.id}
                item={item}
                onToggle={() => onToggleVisibility(item.id)}
                canMoveUp={index > 0}
                canMoveDown={index < items.length - 1}
                onMoveUp={() => handleMoveUp(item.id)}
                onMoveDown={() => handleMoveDown(item.id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

    </div>
  );
};

